import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiCheck, FiChevronDown, FiSearch } from 'react-icons/fi';
import { getChampionshipImageUrl } from '../constants/config';
import { getCityFlagValue, getFlagUrl } from '../utilities/flags';
import { useChampionships } from '../utilities/championships';
import { useI18n } from '../utilities/i18n';

function ChampLogo({ image, className }) {
  const [failed, setFailed] = useState(false);
  const src = getChampionshipImageUrl(image);

  if (!src || failed) {
    return null;
  }

  return (
    <img
      src={src}
      alt=""
      className={className}
      onError={() => setFailed(true)}
    />
  );
}

function CityFlag({ flag, className }) {
  const [failed, setFailed] = useState(false);
  const src = getFlagUrl(flag);

  if (!src || failed) {
    return null;
  }

  return (
    <img
      src={src}
      alt=""
      className={className}
      onError={() => setFailed(true)}
    />
  );
}

function ChampionshipSelector() {
  const { t, lang } = useI18n();
  const { groups, champId, selected, setChampId, loading, error, reload } = useChampionships();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setQuery('');
      if (rootRef.current) {
        rootRef.current.style.removeProperty('--champ-menu-top');
      }
      return undefined;
    }

    const updateMenuPosition = () => {
      if (!rootRef.current || window.innerWidth > 860) {
        if (rootRef.current) {
          rootRef.current.style.removeProperty('--champ-menu-top');
        }
        return;
      }
      const rect = rootRef.current.getBoundingClientRect();
      rootRef.current.style.setProperty('--champ-menu-top', `${Math.round(rect.bottom + 8)}px`);
    };

    const handleClickOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    updateMenuPosition();
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    if (searchRef.current) {
      searchRef.current.focus();
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [open]);

  const getCityName = (city) => (
    lang === 'en'
      ? (city.city || city.city_ru || '')
      : (city.city_ru || city.city || '')
  );

  const filteredGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return groups;

    return groups
      .map((city) => {
        const cityMatched = [city.city, city.city_ru].some((name) => (
          String(name || '').toLowerCase().includes(q)
        ));
        const championships = (city.championships || []).filter((item) => (
          cityMatched || String(item.name || '').toLowerCase().includes(q)
        ));
        return { ...city, championships };
      })
      .filter((city) => city.championships.length > 0);
  }, [groups, query]);

  const handleSelect = (id) => {
    setChampId(id, { persist: true });
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen((prev) => {
      const next = !prev;
      if (next && error && reload) {
        reload();
      }
      return next;
    });
  };

  const triggerLabel = selected?.name || t('championshipPlaceholder');
  const triggerCity = selected ? getCityName(selected) : '';
  const selectedFlag = getCityFlagValue(selected);

  return (
    <div className="champSelector" ref={rootRef}>
      <button
        type="button"
        className={`champSelectorTrigger${open ? ' is-open' : ''}`}
        onClick={handleToggle}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={t('championship')}
      >
        <ChampLogo image={selected?.image} className="champSelectorLogo" />
        {!selected?.image ? (
          <CityFlag flag={selectedFlag} className="champSelectorTriggerFlag" />
        ) : null}
        <span className="champSelectorMeta">
          <span className="champSelectorName">{triggerLabel}</span>
          {triggerCity ? <span className="champSelectorCity">{triggerCity}</span> : null}
        </span>
        <FiChevronDown size="1.1em" color="#edf5ff" />
      </button>

      {open ? (
        <div className="champSelectorMenu">
          <div className="champSelectorSearch">
            <FiSearch size="0.95em" color="rgba(224, 234, 255, 0.62)" />
            <input
              ref={searchRef}
              type="text"
              autoComplete="off"
              spellCheck={false}
              className="champSelectorSearchInput"
              value={query}
              placeholder={t('championshipSearchPh')}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>

          <div className="champSelectorList" role="listbox">
            {loading ? (
              <div className="champSelectorEmpty">{t('championshipsLoading')}</div>
            ) : null}

            {!loading && error ? (
              <div className="champSelectorEmpty">{t('championshipsLoadError')}</div>
            ) : null}

            {!loading && !error && filteredGroups.length === 0 ? (
              <div className="champSelectorEmpty">{t('championshipsEmpty')}</div>
            ) : null}

            {!loading && !error ? filteredGroups.map((city) => (
              <div key={city.city_id || getCityName(city)} className="champSelectorGroup">
                <div className="champSelectorGroupHeader">
                  <CityFlag flag={getCityFlagValue(city)} className="champSelectorCityFlag" />
                  <span>{getCityName(city)}</span>
                </div>
                {city.championships.map((item) => {
                  const isSelected = item.id === champId;
                  return (
                    <button
                      type="button"
                      key={item.id}
                      className={`champSelectorOption${isSelected ? ' is-selected' : ''}`}
                      onClick={() => handleSelect(item.id)}
                      role="option"
                      aria-selected={isSelected}
                    >
                      <span className="champSelectorOptionName">{item.name}</span>
                      {isSelected ? <FiCheck size="1em" color="#3ad1a0" /> : null}
                    </button>
                  );
                })}
              </div>
            )) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default ChampionshipSelector;
